// Export the schedule as a PDF: download it, annotate it, or email it to team staff. The backend
// builds the PDF (report type "rtp_schedule" on the general report endpoint); this dialog only
// collects the filters and the delivery. The buttons and the recipient list follow /reports.
import { lazy, Suspense, useEffect, useState } from "react";
import {
	Button,
	Checkbox,
	ConfigProvider,
	Dropdown,
	Form,
	Input,
	Modal,
	Select,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import {
	coachName,
	rtpApi,
	useCoachList,
	useCurrentUserUuid,
} from "./rtpHooks";
import { getDayCount } from "@utils/rtp_utils";
import { buildWeeks } from "./DayPlansTab";
import type { RtpPlanDetail } from "./types";

const PdfAnnotatorModal = lazy(() =>
	import("@components/PDFAnnotator/PdfAnnotatorModal").then((m) => ({
		default: m.PdfAnnotatorModal,
	})),
);

const MAX_RECIPIENTS = 20;
const MAX_MESSAGE_LENGTH = 1000;

// Tighter field spacing than the antd default, so the dialog stays short.
const COMPACT_FORM_THEME = {
	components: {
		Form: { itemMarginBottom: 12, verticalLabelPadding: "0 0 4px" },
	},
};

type Delivery = "pdf" | "annotate" | "email";

interface ExportFormValues {
	phaseIds: number[];
	weeks: number[];
	ownerUuids: string[];
	recipientUuids: string[];
	message?: string;
}

interface ScheduleExportModalProps {
	plan: RtpPlanDetail;
	open: boolean;
	onClose: () => void;
	defaultPhaseIds: number[];
	defaultOwnerUuids: string[];
	ownerOptions: { uuid: string; name: string }[];
}

export const ScheduleExportModal = ({
	plan,
	open,
	onClose,
	defaultPhaseIds,
	defaultOwnerUuids,
	ownerOptions,
}: ScheduleExportModalProps) => {
	const [form] = Form.useForm<ExportFormValues>();
	// The week filter applies to one phase only, as on the Schedule tab.
	const phaseIds = Form.useWatch("phaseIds", form) ?? [];
	const weekPhase = phaseIds.length === 1 ? plan.phases.find((p) => p.id === phaseIds[0]) : undefined;
	const weekOptions = weekPhase
		? buildWeeks(weekPhase, getDayCount(weekPhase)).map((week, index) => {
				const days = week.filter((cell) => cell.inPhase && cell.date);
				const range = days.length
					? ` · ${days[0].date!.format("MMM D")} – ${days[days.length - 1].date!.format("MMM D")}`
					: "";
				return { value: index + 1, label: `Week ${index + 1}${range}` };
			})
		: [];
	// The delivery whose request is in flight, so only that button spins.
	const [submitting, setSubmitting] = useState<Delivery | null>(null);
	const [annotatorFile, setAnnotatorFile] = useState<{
		buffer: ArrayBuffer;
		name: string;
	} | null>(null);
	const currentUserUuid = useCurrentUserUuid();
	// Only the plan staff can email the schedule; the backend enforces the same rule.
	const canEmail = currentUserUuid in plan.relevantStaff;
	// Same order as /reports: the current user first, then the other staff. A coach without an
	// email address cannot receive the PDF, so the backend rejects them; leave them out.
	const recipients = useCoachList()
		.filter((coach) => coach.email)
		.sort(
			(a, b) =>
				Number(b.uuid === currentUserUuid) - Number(a.uuid === currentUserUuid),
		);

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue({
			phaseIds: defaultPhaseIds,
			weeks: [],
			ownerUuids: defaultOwnerUuids,
			recipientUuids: [],
			message: "",
		});
		// Seed only when the dialog opens, so a plan refetch does not clear the user's choices.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Download and annotate need no recipients, so they read the fields without the email rules.
	const handleSubmit = async (delivery: Delivery) => {
		let values: ExportFormValues;
		try {
			values =
				delivery === "email"
					? await form.validateFields()
					: form.getFieldsValue();
		} catch {
			return;
		}
		const filters = {
			plan_id: plan.id,
			phase_ids: values.phaseIds ?? [],
			weeks: values.phaseIds?.length === 1 ? (values.weeks ?? []) : [],
			session_owner_uuids: values.ownerUuids ?? [],
		};
		setSubmitting(delivery);
		try {
			if (delivery === "email") {
				await rtpApi.exportRtpSchedule({
					mode: "email",
					...filters,
					recipient_uuids: values.recipientUuids,
					message: values.message ?? "",
				});
				message.success("Email sent!");
			} else if (delivery === "annotate") {
				const file = await rtpApi.exportRtpSchedule(
					{ mode: "pdf", ...filters },
					{ returnFile: true },
				);
				setAnnotatorFile(file);
			} else {
				await rtpApi.exportRtpSchedule({ mode: "pdf", ...filters });
				message.success("Schedule downloaded.");
			}
			onClose();
		} catch (error) {
			message.error(
				typeof error === "string"
					? error
					: "Something went wrong — please try again.",
			);
		} finally {
			setSubmitting(null);
		}
	};

	return (
		<>
			<Modal
				title="Export Schedule"
				width={640}
				open={open}
				onCancel={onClose}
				destroyOnHidden
				footer={[
					<Dropdown
						key="download"
						menu={{
							items: [
								{
									key: "pdf",
									label: (
										<span data-attr="settings.rtp-schedule-export.download">
											Download Only
										</span>
									),
								},
								{
									key: "annotate",
									label: (
										<span data-attr="settings.rtp-schedule-export.annotate">
											Annotate + Download
										</span>
									),
								},
							],
							onClick: (info) => handleSubmit(info.key as Delivery),
						}}
						placement="bottomRight"
						disabled={submitting !== null}
					>
						<Button
							data-attr="settings.rtp-schedule-export.open-download-menu"
							loading={submitting === "pdf" || submitting === "annotate"}
							disabled={submitting !== null}
						>
							Download Now <DownOutlined />
						</Button>
					</Dropdown>,
					canEmail ? (
						<Button
							key="send"
							type="primary"
							data-attr="settings.rtp-schedule-export.send"
							loading={submitting === "email"}
							disabled={submitting !== null}
							onClick={() => handleSubmit("email")}
						>
							Send Email
						</Button>
					) : null,
				]}
			>
				<ConfigProvider theme={COMPACT_FORM_THEME}>
					<Form
						form={form}
						layout="vertical"
						onValuesChange={(changed) => {
							// Week numbers belong to one phase, so a new phase choice clears them.
							if ("phaseIds" in changed) form.setFieldValue("weeks", []);
						}}
					>
						<Form.Item name="phaseIds" label="Phases">
							<Select
								mode="multiple"
								allowClear
								placeholder="All phases"
								options={plan.phases.map((phase, index) => ({
									value: phase.id,
									label: `Phase ${index + 1} · ${phase.name}`,
								}))}
							/>
						</Form.Item>
						{weekPhase ? (
							<Form.Item name="weeks" label="Weeks">
								<Select mode="multiple" allowClear placeholder="All weeks" options={weekOptions} />
							</Form.Item>
						) : null}
						{ownerOptions.length > 0 ? (
							<Form.Item name="ownerUuids" label="Show blocks by">
								<Select
									mode="multiple"
									allowClear
									placeholder="All coaches"
									options={ownerOptions.map((owner) => ({
										value: owner.uuid,
										label: owner.name,
									}))}
								/>
							</Form.Item>
						) : null}
						{canEmail ? (
							<>
								<Form.Item
									name="recipientUuids"
									label="Email recipients"
									rules={[
										{
											required: true,
											message: "Choose at least one recipient",
										},
										{
											type: "array",
											max: MAX_RECIPIENTS,
											message: `Choose ${MAX_RECIPIENTS} recipients or fewer`,
										},
									]}
								>
									<Checkbox.Group className="rtp-export-recipients">
										{recipients.map((coach) => (
											<Checkbox key={coach.uuid} value={coach.uuid}>
												{coachName(coach)}{" "}
												<span className="rtp-export-recipient-email">
													({coach.email})
												</span>
											</Checkbox>
										))}
									</Checkbox.Group>
								</Form.Item>
								<Form.Item name="message" label="Message">
									<Input.TextArea
										rows={2}
										maxLength={MAX_MESSAGE_LENGTH}
										placeholder="Optional note to staff"
									/>
								</Form.Item>
							</>
						) : null}
					</Form>
				</ConfigProvider>
			</Modal>
			{annotatorFile ? (
				<Suspense fallback={null}>
					<PdfAnnotatorModal
						open
						initialFile={annotatorFile}
						onClose={() => setAnnotatorFile(null)}
					/>
				</Suspense>
			) : null}
		</>
	);
};
